import { useCallback, useEffect, useRef, useState } from "react";

interface UsePollingOptions<TData> {
	intervalMs?: number;
	autoStart?: boolean;
	shouldContinue?: (result: TData) => boolean;
	onSuccess?: (result: TData) => void;
	onError?: (error: unknown) => void;
}

interface UsePollingResult<TData> {
	data: TData | null;
	error: unknown;
	isPolling: boolean;
	startPolling: () => void;
	stopPolling: () => void;
	refreshNow: () => Promise<TData | null>;
}

const DEFAULT_INTERVAL_MS = 5000;

const usePolling = <TData>(
	pollFn: () => Promise<TData>,
	options: UsePollingOptions<TData> = {}
): UsePollingResult<TData> => {
	const { intervalMs = DEFAULT_INTERVAL_MS, autoStart = true, shouldContinue, onSuccess, onError } = options;

	const [data, setData] = useState<TData | null>(null);
	const [error, setError] = useState<unknown>(null);
	const [isPolling, setIsPolling] = useState<boolean>(false);

	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const pollFnRef = useRef(pollFn);
	const shouldContinueRef = useRef(shouldContinue);
	const onSuccessRef = useRef(onSuccess);
	const onErrorRef = useRef(onError);

	// Keep refs current on every render so callbacks are always up to date
	pollFnRef.current = pollFn;
	shouldContinueRef.current = shouldContinue;
	onSuccessRef.current = onSuccess;
	onErrorRef.current = onError;

	const stopPolling = useCallback((): void => {
		if (intervalRef.current !== null) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
		setIsPolling(false);
	}, []);

	const refreshNow = useCallback(async (): Promise<TData | null> => {
		try {
			const result = await pollFnRef.current();
			setData(result);
			setError(null);
			onSuccessRef.current?.(result);

			if (shouldContinueRef.current && !shouldContinueRef.current(result)) {
				stopPolling();
			}

			return result;
		} catch (pollError) {
			setError(pollError);
			onErrorRef.current?.(pollError);
			return null;
		}
	}, [stopPolling]);

	const startPolling = useCallback((): void => {
		if (intervalRef.current !== null) {
			return;
		}
		setIsPolling(true);
		void refreshNow();
		intervalRef.current = setInterval(() => void refreshNow(), intervalMs);
	}, [intervalMs, refreshNow]);

	useEffect(() => {
		if (autoStart) {
			startPolling();
		}
		return () => stopPolling();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return { data, error, isPolling, startPolling, stopPolling, refreshNow };
};

export default usePolling;
